/**
 * Shared Marketing Banners types and the locale-agnostic contract consumed by
 * the presentational components in this module.
 *
 * Both apps (portal: i18n + react-query; backoffice: static EN strings + server
 * actions) feed these components the same shapes:
 *  - a `MarketingBannersTranslations` object (portal resolves it from i18n,
 *    backoffice supplies a static English object),
 *  - a `getLanguageLabel(code)` function (portal binds i18n `t`, backoffice uses
 *    its English-only map),
 *  - plain callbacks for actions (download / rename / view / copy) so the shared
 *    components never import server actions, react-query, react-i18next, or next/*.
 */

import type { Campaign } from "../api/generated";
import type { LastClickCellTranslations } from "../ReferralLinkModals";

export type MarketingBannerType = "html_ad" | "image_banner";

export type MarketingBannerRow = {
  id: string;
  /** Banner display name shown in the table. */
  name: string;
  /** Raw HTML snippet, rendered as iframe preview and copied to clipboard. */
  creative_code: string;
  /** Banner kind — drives icon, copy label, download format, etc. */
  type: MarketingBannerType;
  /** Tune offer ID the banner belongs to — used to download the banner image. */
  offer_id: string;
  /** Creative file ID (marketing_assets.file_id) — sent to download the banner image. */
  creative_file_id: string;
  /** Banner file name — used as the saved download filename. */
  creative_file_name: string;
  /** Banner group / campaign the banner belongs to. */
  banner_group: string;
  /** Banner asset width in pixels. */
  width: number;
  /** Banner asset height in pixels. */
  height: number;
  /** ISO 639 language code (e.g. "en"). */
  language: string;
  /** Affiliate ID for the rebate program (e.g. "345" → "HOID 345"). */
  hoid: string;
  /** ISO 8601 creation timestamp. */
  created_at: string;
  /**
   * Most recent click timestamp (ISO 8601, hour precision), or null if the
   * banner has never been clicked. Gated behind the `part-3344-performance-report`
   * flag by the consuming column/row.
   */
  last_clicked_at?: string | null;
  /** Campaign associated with the banner; null when unattached. */
  campaign?: Pick<Campaign, "id" | "name"> | null;
};

/** Resolves an ISO language code to a human label (e.g. "en" → "English"). */
export type GetLanguageLabel = (code: string) => string;

/**
 * Preferred default banner creative size (width×height in pixels). Used as the
 * thumbnail in the banner library and the initial size selected in the
 * configuration modal when a matching asset is available.
 */
export const DEFAULT_BANNER_SIZE = { width: 300, height: 250 } as const;

/** Max character length for a banner name (matches the Figma input affordance). */
export const RENAME_CHAR_LIMIT = 60;

/**
 * The subset of a rename-banner hook the shared modals need. Both apps'
 * `useRenameBanner` return a superset of this, so the full hook value can be
 * passed straight through.
 */
export type RenameBannerHandlers = {
  name: string;
  error: string;
  isSaving: boolean;
  isSaveDisabled: boolean;
  startRename: (record: MarketingBannerRow) => void;
  changeName: (value: string) => void;
  save: () => Promise<boolean>;
};

/** Async (or sync) download of a banner's image; rejects on failure. */
export type DownloadBanner = (
  banner: MarketingBannerRow,
) => void | Promise<void>;

/**
 * Canonical translation shape for the Marketing Banners UI. Portal's
 * `useMarketingBannersTranslations` hook returns this; the backoffice's static
 * `MARKETING_BANNERS_TRANSLATIONS` object satisfies it. Portal is the source of
 * truth for the shape.
 */
export type MarketingBannersTranslations = {
  header: {
    title: string;
    subtitle: string;
  };
  toolbar: {
    searchPlaceholder: string;
    filterButton: string;
    exportButton: string;
    addButton: string;
    sortBy: string;
  };
  toasts: {
    exportError: { title: string; description: string };
    downloadError: { title: string; description: string };
    highlightNotFound: { title: string; description: string };
  };
  columns: {
    bannerName: string;
    type: string;
    details: string;
    rebateProgram: string;
    campaign: string;
    noCampaign: string;
    created: string;
    lastClick: string;
    lastClickTooltip: string;
  };
  lastClickCell: LastClickCellTranslations;
  states: {
    empty: { title: string; subtitle: string };
    error: { title: string; subtitle: string; refreshButton: string };
  };
  actions: {
    copyCode: string;
    copied: string;
    getCopyLabels: (type: string) => { copyLabel: string; copiedLabel: string };
    view: string;
    mobileView: string;
    rename: string;
    downloadPng: string;
    /**
     * Label for the "View performance" affordance. Optional so the
     * backoffice's static translations object (which has no Performance
     * Report page to link to) satisfies this type unchanged (AD-5).
     */
    viewPerformance?: string;
  };
  preview: {
    sectionTitle: string;
    labels: {
      banner: string;
      size: string;
      language: string;
      type: string;
      rebateProgram: string;
      campaign: string;
      created: string;
      lastClicked: string;
    };
    types: Record<string, string>;
    expand: string;
    collapse: string;
  };
  rename: {
    title: string;
    save: string;
    cancel: string;
    duplicateError: string;
    genericError: string;
  };
  filter: {
    title: string;
    titleMobile: string;
    apply: string;
    clear: string;
    reset: string;
    banner: { label: string; placeholder: string };
    language: { label: string; placeholder: string };
    size: { label: string; placeholder: string };
    type: { label: string; placeholder: string };
    rebateProgram: { label: string; placeholder: string };
    campaign: {
      label: string;
      placeholder: string;
      noCampaignOption: string;
    };
  };
  sortSheet: {
    title: string;
    created: string;
    apply: string;
    newestFirst: string;
    oldestFirst: string;
    sortOrder: string;
    createdNewestFirst: string;
    createdOldestFirst: string;
  };
};
